import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { NotificationPreferenceChange } from '@/lib/api/domains/notification-preferences';
import type { UserId } from '@/lib/api/ids';
import { NOTIFICATION_PREFERENCE_LABELS } from '../schemas/notification-preferences';
import { notificationPreferenceHistoryQuery } from '../api/notification-preference-queries';

/*
 * The notification-preference CHANGE HISTORY surface (F02 R28/R29). For the
 * signed-in user (portal) or an account opened from its record, it shows, per
 * changed choice:
 *
 *   - WHO changed it (AC-bf8668ea / AC-58e727f7): a SELF-change reads as the
 *     user's own ("You"); an ON-BEHALF change names the PERSON who made it
 *     (changedByName) — never the organization (R28, feature design §3). When
 *     the server could not establish who acted (changedByName null, R28's F69
 *     edge), the entry names no person and still never the organization;
 *   - what the choice WAS BEFORE and what it is now (AC-b1d8996b): the
 *     before-value from the TPA-owned change record, shown as On/Off, or
 *     "not set" for a never-answered-before change (a null before-value), and
 *     the after-value.
 *
 * It reads `notificationPreferenceHistoryQuery(userId)`; the server resolves the
 * changer's NAME and the before/after. The id is opaque, never PII; the name is
 * shown, never sent (AGENTS.md — ids only in requests/URLs/telemetry). The
 * server owns the record and the attribution; this surface renders what it
 * returns.
 */
export function NotificationPreferenceHistory({ userId }: { userId: UserId }) {
  const query = useQuery(notificationPreferenceHistoryQuery(userId));
  const headingId = useId();
  const changes = query.data;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h2 id={headingId} className="text-base font-medium">
        Notification change history
      </h2>

      {!changes ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : changes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No changes yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {changes.map((change, index) => (
            <li
              key={`${change.key}-${change.changedAt}-${index}`}
              data-testid={`preference-change-${change.key}`}
              className="flex flex-col gap-1 text-sm"
            >
              <span className="font-medium">{labelFor(change.key)}</span>
              <span className="text-muted-foreground">
                {describeChange(change)}
              </span>
              <span className="text-muted-foreground">{changedBy(change)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// The human label for a governed key; fall back to the key itself so an
// unexpected key is still rendered rather than blank.
function labelFor(key: NotificationPreferenceChange['key']): string {
  return NOTIFICATION_PREFERENCE_LABELS[key] ?? key;
}

// On/Off for a stored answer; "not set" for a never-answered-before change (a
// null before-value) — the default was in force, so the history shows there was
// no prior answer rather than a fabricated value (AC-b1d8996b).
function describeChange(change: NotificationPreferenceChange): string {
  const before =
    change.enabledBefore === null ? 'not set' : onOff(change.enabledBefore);
  return `Changed from ${before} to ${onOff(change.enabledAfter)}`;
}

function onOff(enabled: boolean): string {
  return enabled ? 'On' : 'Off';
}

// WHO changed it: a self-change reads as the user's own ("You"); an on-behalf
// change names the PERSON (changedByName), never the organization. When the
// server could not establish who acted (changedByName null), name no person.
function changedBy(change: NotificationPreferenceChange): string {
  if (change.self) return 'Changed by You';
  if (change.changedByName) return `Changed by ${change.changedByName}`;
  return 'Changed by someone else';
}
